/**
 * HubWizard — Event Hub creation/edit wizard, mirroring the Events wizard.
 * Steps: Hub Basics · Hub Page · Review & publish · Live.
 *
 * Publish makes the hub page live; once live the page DESIGN locks until the
 * user takes it offline — but adding/removing events (the hub's whole purpose)
 * stays available live, on both the Page and Live steps.
 */
import { type ReactNode, useEffect, useRef, useState } from "react";
import { alpha, Btn, Card, Icon, IconBtn, Input, Pill, T, tx } from "@v0y4ge/jrni-ui";
import { Screen } from "../../shell/chrome";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { useEvents } from "../events/useEvents";
import { saveHub, publishHubOp, refetchHubs, type Hub, type HubKind } from "./hubs-data";
import { HubCreationStore, useHubDraft, requiredHubChecks, hubIsLive, hubSlugify, HUB_FLOW, type HubDraft } from "./hub-creation";
import {
  landingBlocksFor, LANDING_THEMES, LandingPreview, BlockInspector,
  findBlockDeep, parentRowOf, updateCfgDeep, removeBlockDeep, moveBlockDeep,
  insertBeforeDeep, appendToCellDeep, getRowCells, CELL_FORBIDDEN, cloneBlockDeep, insertAfterDeep,
  type LandingBlock, type LandingTheme, type HubEventCard,
} from "../landing/landing-builder-helpers";
import { publicHost, publicUrl as composePublicUrl } from "../../lib/public-url";
import type { UIEvent } from "../../lib/events-adapter";
import { useT, t as i18nT } from "../../lib/i18n";

const KIND_OPTS: { kind: HubKind; t: string; d: string; icon: ReactNode }[] = [
  { kind: "workspace", t: "Workspace hub", d: "Every published event across your workspace, in one catalog.", icon: <Icon.globe size={18} /> },
  { kind: "location", t: "Location hub", d: "All events at a single venue or city.", icon: <Icon.pin size={18} /> },
  { kind: "group", t: "Series hub", d: "A curated set of related events, promoted together.", icon: <Icon.layers size={18} /> },
];

/** Derive the Hub-shaped object the preview / filter helpers expect. */
function draftToHub(d: HubDraft): Hub {
  return {
    id: d._editingId || "draft", kind: d.kind, name: d.name || "Untitled hub", slug: d.slug || "untitled",
    description: d.description ?? "", events: 0, visits: d.visits ?? 0, conv: d.conv ?? 0,
    status: d._published ? "published" : "draft", edited: "Just now", custom: d.custom || "—",
    filterRule: d.filterRule, branding: d.branding, seo: d.seo, visibility: d.visibility,
    publicUrl: d.publicUrl ?? null, publicHost: d.publicHost ?? null,
  };
}
/** Events shown on the hub = filter-rule auto-pull + manual adds − manual removes. */
// ── Mini preview ─────────────────────────────────────────────────────────────
// ── Events-shown rail (add / remove; usable while live) ──────────────────────
function EventsRail({ d, all, canEdit }: { d: HubDraft; all: UIEvent[]; canEdit: boolean }) {
  const t = useT();
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");
  const inIds = new Set(d.manualEventIds);
  const shown = all.filter((e) => inIds.has(e.id));
  const shownIds = inIds;
  const candidates = all.filter((e) => !shownIds.has(e.id) && (!q.trim() || e.title.toLowerCase().includes(q.toLowerCase())));
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: T.n1, margin: 0 }}>{t("hubs.wizard.eventsShown", "Events shown ·")}{shown.length}</h3>
        {canEdit ? <button onClick={() => setAdding((v) => !v)} style={{ border: "none", background: "none", color: T.p1, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>{adding ? t("common.actions.done", "Done") : t("dashboard.root.label", "Add event")}</button> : null}
      </div>
      <p style={{ fontSize: 12, color: T.soft, lineHeight: 1.5, margin: "0 0 12px" }}>{t("hubs.wizard.autoPulledFromTheHubRulesPlusAnyYouAddAddingOr", "Auto-pulled from the hub rules, plus any you add. Adding or removing events is allowed even while the hub is live.")}</p>
      {adding && canEdit ? (
        <div style={{ marginBottom: 12, border: `1px solid ${T.p2}`, borderRadius: 8, overflow: "hidden" }}>
          <Input placeholder={t("hubs.wizard.findAnEventToAdd", "Find an event to add…")} value={q} onChange={(e) => setQ(e.target.value)} style={{ border: "none", borderRadius: 0, borderBottom: `1px solid ${T.border}` }} />
          <div style={{ maxHeight: 220, overflow: "auto" }}>
            {candidates.length ? candidates.map((ev) => (
              <button key={ev.id} onClick={() => HubCreationStore.addEvent(ev.id)} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", padding: "8px 10px", border: "none", borderBottom: `1px solid ${T.n6}`, background: T.white, cursor: "pointer", fontFamily: T.font }}>
                <span style={{ width: 22, height: 22, borderRadius: 4, background: ev.hue, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{ev.title.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
                <span style={{ flex: 1, fontSize: 12.5, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ev.title}</span>
                <Icon.plus size={13} style={{ color: T.p1 }} />
              </button>
            )) : <div style={{ padding: 14, fontSize: 12, color: T.soft, textAlign: "center" }}>{t("hubs.wizard.noMoreEventsToAdd", "No more events to add.")}</div>}
          </div>
        </div>
      ) : null}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {shown.map((ev) => (
          <div key={ev.id} style={{ display: "flex", gap: 10, padding: 10, border: `1px solid ${T.border}`, borderRadius: 6, alignItems: "center" }}>
            <span style={{ width: 28, height: 28, borderRadius: 4, background: ev.hue, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 11, flexShrink: 0 }}>{ev.title.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ev.title}</div>
              <div style={{ fontSize: 11, color: T.soft }}>{ev.location.split(" · ")[0]}</div>
            </div>
            {canEdit ? <IconBtn size={26} label={t("hubs.wizard.removeFromHub", "Remove from hub")} onClick={() => HubCreationStore.removeEvent(ev.id)}><Icon.x size={12} /></IconBtn> : null}
          </div>
        ))}
        {!shown.length ? <div style={{ padding: 16, textAlign: "center", color: T.soft, fontSize: 12, border: `1.5px dashed ${T.n4}`, borderRadius: 8 }}>{t("hubs.wizard.noEventsYet", "No events yet.")}{" "}{canEdit ? t('hubs.wizard.text') : ""}</div> : null}
      </div>
    </div>
  );
}

// ── Field helpers ────────────────────────────────────────────────────────────
function Field({ label, children, hint, required }: { label: string; children: ReactNode; hint?: string; required?: boolean }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: T.n1, marginBottom: 5 }}>{label}{required ? <span style={{ color: T.red1, marginLeft: 3 }}>*</span> : null}</label>
      {children}
      {hint ? <div style={{ fontSize: 11, color: T.soft, marginTop: 4 }}>{hint}</div> : null}
    </div>
  );
}
const inputStyle: React.CSSProperties = { width: "100%", height: 38, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 6, fontSize: 13, fontFamily: T.font, color: T.n1, outline: "none", boxSizing: "border-box" };
const SECTION: React.CSSProperties = { fontSize: 11, color: T.soft, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 };

// ── Shell ────────────────────────────────────────────────────────────────────
function HubWizardShell({ step, children, footer }: { step: number; children: ReactNode; footer?: ReactNode }) {
  const t = useT();
  const nav = useNav();
  const d = useHubDraft();
  const isEditing = !!d._editingId;
  const live = hubIsLive(d);
  const PHASES = [
    { id: 0, label: t("hubs.wizard.hubBasics", "Hub Basics"), route: "hub.create.basics" },
    { id: 1, label: t("hubs.wizard.hubPage", "Hub Page"), route: "hub.create.page" },
    { id: 2, label: t("hubs.wizard.reviewPublish", "Review & publish"), route: "hub.create.publish" },
    { id: 3, label: t("common.status.live", "Live"), route: "hub.create.live" },
  ];
  const back = step > 0 ? HUB_FLOW[step - 1] : "hub.pages";
  const takeOffline = () => { HubCreationStore.takeOffline(); toast({ tone: "warning", title: t("hubs.wizard.hubTakenOffline", "Hub taken offline"), subtitle: t("hubs.wizard.editThePageThenRePublishToGoLiveAgain", "Edit the page, then re-publish to go live again.") }); };
  return (
    <Screen rail="events" topbar>
      <div style={{ padding: "16px 24px", background: T.white, borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <h1 style={{ ...tx.h3, fontWeight: 600 }}>{isEditing ? t('hubs.wizard.text2') : t('hubs.wizard.text3')}</h1>
          {!d._published ? <Pill tone="neutral">{d.name || "Untitled hub"}</Pill> : null}
        </div>
        {d._published ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700, color: T.green1, background: alpha(T.green1, "14"), border: `1px solid ${alpha(T.green1, "55")}`, borderRadius: 999, padding: "4px 12px" }}><span style={{ width: 7, height: 7, borderRadius: 999, background: T.green1 }} /> {t("common.status.live", "Live")}</span>
            <Btn variant="secondary" size="sm" icon={<Icon.lock size={13} />} onClick={takeOffline}>{t("hubs.wizard.takeOffline", "Take offline")}</Btn>
          </div>
        ) : null}
      </div>
      {/* Phase tracker */}
      <div style={{ padding: "16px 24px", background: T.white, borderBottom: `1px solid ${T.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
          {PHASES.map((p, i) => {
            const done = p.id < step; const here = p.id === step;
            return (
              <div key={p.label} style={{ display: "contents" }}>
                <div onClick={() => nav.go(p.route)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexShrink: 0 }}>
                  <span style={{ width: 24, height: 24, borderRadius: 999, background: done ? T.p1 : T.white, color: done ? T.white : here ? T.p1 : T.n3, border: done ? "none" : `1.5px solid ${here ? T.p1 : T.n4}`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600 }}>{done ? <Icon.check size={12} /> : i + 1}</span>
                  <span style={{ fontSize: 13, fontWeight: here ? 600 : 500, color: here || done ? T.n1 : T.soft, whiteSpace: "nowrap" }}>{p.label}</span>
                </div>
                {i < PHASES.length - 1 ? <span style={{ flex: 1, minWidth: 12, height: 1, background: done ? T.p1 : T.n4, margin: "0 10px" }} /> : null}
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ flex: 1, overflow: "auto", background: T.canvas, display: "flex", flexDirection: "column" }}>
        {live && step !== 3 ? (
          <div style={{ margin: "16px 24px 0", padding: "12px 16px", background: T.orange2, border: `1px solid ${alpha(T.orange1, "55")}`, borderRadius: 10, display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
            <span style={{ width: 30, height: 30, borderRadius: 8, background: T.orange1, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.lock size={15} /></span>
            <div style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{t("hubs.wizard.thisHubIsLiveEditingIsLocked", "This hub is live — editing is locked")}</div>
            <Btn icon={<Icon.arrow dir="right" size={14} />} onClick={() => nav.go("hub.create.live")}>{t("hubs.wizard.goToLive", "Go to Live")}</Btn>
          </div>
        ) : null}
        {children}
      </div>
      <div style={{ padding: "12px 24px", background: T.white, borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Btn variant="secondary" icon={<Icon.arrow dir="left" size={14} />} onClick={() => nav.go(back)}>{t("common.actions.back", "Back")}</Btn>
        {footer}
      </div>
    </Screen>
  );
}

// ── Step 1 · Basics ──────────────────────────────────────────────────────────
export function HubCreateBasics() {
  const t = useT();
  const nav = useNav();
  const d = useHubDraft();
  const live = hubIsLive(d);
  const setName = (name: string) => HubCreationStore.patch({ name, ...(!d._editingId && !d.slug ? {} : {}), slug: d.slug || hubSlugify(name) });
  const host = publicHost("attend");
  return (
    <HubWizardShell step={0} footer={<Btn iconRight={<Icon.arrow size={14} />} disabled={!d.name.trim()} onClick={() => nav.go("hub.create.page")}>{t("common.actions.continue", "Continue")}</Btn>}>
      <div style={{ maxWidth: 640, margin: "0 auto", width: "100%", padding: "24px 24px 40px", display: "flex", flexDirection: "column", gap: 22 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, ...(live ? { pointerEvents: "none", opacity: 0.6 } : {}) }}>
          <Field label={t("hubs.wizard.hubName", "Hub name")} required>
            <input value={d.name} placeholder={t("hubs.wizard.eGAcmeEvents", "e.g. Acme Events")} onChange={(e) => setName(e.target.value)} style={inputStyle} />
          </Field>
          <Field label={t("hubs.wizard.hubType", "Hub type")} hint={t('hubs.wizard.hint')}>
            <div style={{ display: "grid", gap: 8 }}>
              {KIND_OPTS.map((o) => {
                const on = d.kind === o.kind;
                return (
                  <button key={o.kind} onClick={() => HubCreationStore.patch({ kind: o.kind, filterRule: { kind: o.kind } })} style={{ display: "flex", gap: 12, padding: 12, borderRadius: 8, border: `1.5px solid ${on ? T.p1 : T.border}`, background: on ? T.p4 : T.white, cursor: "pointer", textAlign: "left", fontFamily: T.font }}>
                    <span style={{ width: 36, height: 36, borderRadius: 8, background: on ? T.p1 : T.n6, color: on ? T.white : T.n2, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{o.icon}</span>
                    <span><span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{o.t}</span><span style={{ display: "block", fontSize: 12, color: T.soft, marginTop: 2 }}>{o.d}</span></span>
                    {on ? <Icon.check size={16} style={{ color: T.p1, marginLeft: "auto" }} /> : null}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label={t("hubs.wizard.visibility", "Visibility")} hint={d.visibilityMode === "public" ? "Anyone with the link can view; indexable by search engines." : "Visitors must sign in to view and register."}>
            <div style={{ display: "flex", gap: 8 }}>
              {(["public", "private"] as const).map((m) => {
                const on = d.visibilityMode === m;
                return <button key={m} onClick={() => HubCreationStore.patch({ visibilityMode: m, visibility: { ...d.visibility, indexed: m === "public", requireSignIn: m === "private" } })} style={{ flex: 1, padding: "10px 12px", borderRadius: 6, border: `1.5px solid ${on ? T.p1 : T.border}`, background: on ? T.p4 : T.white, color: on ? T.p1 : T.n1, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: T.font, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>{m === "public" ? <Icon.globe size={14} /> : <Icon.lock size={14} />}{m === "public" ? t("events.detailOverlay.public", "Public") : t("events.detailShareTab.private", "Private")}</button>;
              })}
            </div>
          </Field>
          <Field label={t("hubs.wizard.urlSlug", "URL slug")}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 13, color: T.soft, fontFamily: T.fontMono }}>{host}/</span>
              <input value={d.slug} placeholder={t("hubs.wizard.acmeEvents", "acme-events")} onChange={(e) => HubCreationStore.patch({ slug: hubSlugify(e.target.value) })} style={{ ...inputStyle, fontFamily: T.fontMono }} />
            </div>
          </Field>
          <Field label={t("hubs.wizard.description", "Description")} required hint={t('hubs.wizard.hint2')}>
            <textarea value={d.description ?? ""} rows={3} placeholder={t("hubs.wizard.oneLineAboutThisHub", "One line about this hub…")} onChange={(e) => HubCreationStore.patch({ description: e.target.value })} style={{ ...inputStyle, height: "auto", padding: "10px 12px", resize: "vertical", lineHeight: 1.5 }} />
          </Field>
        </div>
      </div>
    </HubWizardShell>
  );
}


// ── Step 2 · Page (block editor — same engine as landing pages) ──────────────
function toEventCards(events: UIEvent[]): HubEventCard[] {
  return events.map((e) => ({ id: e.id, title: e.title, date: (e.date || "").split(" ").slice(1, 3).join(" "), location: e.location.split(" · ")[0], hue: e.hue, tag: (e.tags && e.tags[0]) || e.format }));
}
const uid = () => `b${Date.now().toString(36)}${((Math.random() * 1e6) | 0).toString(36)}`;

export function HubCreatePage() {
  const t = useT();
  const nav = useNav();
  const d = useHubDraft();
  const { events: all } = useEvents();
  const live = hubIsLive(d);
  const events = all.filter((e) => d.manualEventIds.includes(e.id));
  const eventCards = toEventCards(events);
  const blocks = d.pageBlocks;
  const [selectedUid, setSelectedUid] = useState<string | null>(blocks[0]?.uid || null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const dragRef = useRef<{ type: string | null; uid: string | null }>({ type: null, uid: null });
  // Resizable/responsive canvas (same as the landing editor): drag either page
  // edge to test any width; the preview's container query reflows responsively.
  const [vw, setVw] = useState<number | null>(null);
  const [liveW, setLiveW] = useState(0);
  const [draggingEdge, setDraggingEdge] = useState(false);
  const canvasScrollRef = useRef<HTMLDivElement>(null);
  const pageColRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = pageColRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setLiveW(Math.round(el.getBoundingClientRect().width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const startEdgeDrag = (side: 1 | -1) => (e: React.PointerEvent) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startW = pageColRef.current?.getBoundingClientRect().width || 900;
    const maxW = Math.max(360, (canvasScrollRef.current?.clientWidth || 1400) - 56);
    setDraggingEdge(true);
    const move = (ev: PointerEvent) => { const next = Math.round(startW + (ev.clientX - startX) * side * 2); setVw(Math.min(maxW, Math.max(320, next))); };
    const up = () => { setDraggingEdge(false); window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const palette = landingBlocksFor("hub");

  const currentTheme: LandingTheme = LANDING_THEMES.find((t) => t.id === d.pageTheme)
    || { id: "brand", name: t("hubs.wizard.brand", "Brand"), brand: d.branding.primaryColor || T.p1, hero: [d.branding.primaryColor || "#75ADFA", d.branding.primaryColor || T.p1], font: "DM Sans" };
  const selected = findBlockDeep(blocks, selectedUid);
  const setBlocks = (next: LandingBlock[]) => HubCreationStore.setPage({ pageBlocks: next });

  const spawn = (type: string): LandingBlock | null => {
    const def = palette.find((b) => b.id === type); if (!def) return null;
    const cfg = type === "row" ? { ratio: "1-1", gap: 20, stackMobile: true, cells: [[], []] as LandingBlock[][] } : { ...def.defaults };
    return { uid: uid(), type, cfg };
  };
  const singleton = (type: string) => type === "hero" || type === "events";
  const addBlock = (type: string) => {
    if (singleton(type) && blocks.some((b) => b.type === type)) { setSelectedUid(blocks.find((b) => b.type === type)!.uid); return; }
    const nb = spawn(type); if (!nb) return;
    setBlocks([...blocks, nb]); setSelectedUid(nb.uid);
  };
  const updateCfg = (id: string, patch: Record<string, unknown>) => setBlocks(updateCfgDeep(blocks, id, patch));
  const removeBlock = (id: string) => { const next = removeBlockDeep(blocks, id); setBlocks(next); if (id === selectedUid) setSelectedUid(next[0]?.uid || null); };
  const moveBlock = (id: string, dir: number) => setBlocks(moveBlockDeep(blocks, id, dir));
  const duplicate = (id: string) => { const b = findBlockDeep(blocks, id); if (!b || singleton(b.type)) return; const clone = cloneBlockDeep(b); setBlocks(insertAfterDeep(blocks, id, clone)); setSelectedUid(clone.uid); };
  const onDropOnBlock = (targetUid: string) => {
    const src = dragRef.current; dragRef.current = { type: null, uid: null }; setDragOver(null);
    const intoCell = !!parentRowOf(blocks, targetUid);
    if (src.type) {
      if (intoCell && CELL_FORBIDDEN.has(src.type)) return;
      if (singleton(src.type) && blocks.some((b) => b.type === src.type)) return;
      const nb = spawn(src.type); if (!nb) return;
      setBlocks(insertBeforeDeep(blocks, targetUid, nb)); setSelectedUid(nb.uid);
    } else if (src.uid && src.uid !== targetUid) {
      const found = findBlockDeep(blocks, src.uid); if (!found) return;
      if (intoCell && CELL_FORBIDDEN.has(found.type)) return;
      if (found.type === "row" && findBlockDeep(([] as LandingBlock[]).concat(...getRowCells(found.cfg)), targetUid)) return;
      setBlocks(insertBeforeDeep(removeBlockDeep(blocks, src.uid), targetUid, found)); setSelectedUid(found.uid);
    }
  };
  const onDropToCell = (rowUid: string, cellIdx: number) => {
    const src = dragRef.current; dragRef.current = { type: null, uid: null }; setDragOver(null);
    if (src.type) { if (CELL_FORBIDDEN.has(src.type)) return; const nb = spawn(src.type); if (!nb) return; setBlocks(appendToCellDeep(blocks, rowUid, cellIdx, nb)); setSelectedUid(nb.uid); }
    else if (src.uid && src.uid !== rowUid) { const found = findBlockDeep(blocks, src.uid); if (!found || CELL_FORBIDDEN.has(found.type)) return; setBlocks(appendToCellDeep(removeBlockDeep(blocks, src.uid), rowUid, cellIdx, found)); setSelectedUid(found.uid); }
  };

  const designLock = live ? { pointerEvents: "none" as const, opacity: 0.55 } : undefined;

  return (
    <HubWizardShell step={1} footer={<Btn iconRight={<Icon.arrow size={14} />} onClick={() => nav.go("hub.create.publish")}>{t("common.actions.continue", "Continue")}</Btn>}>
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "232px 1fr 328px", minHeight: 0 }}>
        {/* Left · block palette + theme */}
        <div style={{ borderRight: `1px solid ${T.border}`, background: T.white, overflow: "auto", padding: "16px 14px 40px" }}>
          {live ? <div style={{ marginBottom: 12, fontSize: 12, color: T.soft, display: "flex", alignItems: "center", gap: 6, padding: "8px 10px", background: T.n6, borderRadius: 6 }}><Icon.lock size={12} /> {t("hubs.wizard.takeTheHubOfflineTopRightToEditThePage", "Take the hub offline (top right) to edit the page.")}</div> : null}
          <div style={designLock}>
            <div style={SECTION}>{t("hubs.wizard.addBlocks", "Add blocks")}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 20 }}>
              {palette.map((b) => (
                <button key={b.id} draggable onClick={() => addBlock(b.id)}
                  onDragStart={() => { dragRef.current = { type: b.id, uid: null }; }}
                  onDragEnd={() => { dragRef.current = { type: null, uid: null }; setDragOver(null); }}
                  style={{ padding: "10px 8px", border: `1px solid ${T.border}`, borderRadius: 7, background: T.white, cursor: "grab", fontSize: 12, fontWeight: 600, color: T.n1, fontFamily: T.font, textAlign: "center" }}>{b.l}</button>
              ))}
            </div>
            <div style={SECTION}>{t("hubs.wizard.theme", "Theme")}</div>
            <select value={d.pageTheme} onChange={(e) => HubCreationStore.setPage({ pageTheme: e.target.value })} style={{ ...inputStyle, cursor: "pointer", appearance: "auto" }}>
              <option value="brand">{t("hubs.wizard.brandFromHubColor", "Brand (from hub color)")}</option>
              {LANDING_THEMES.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <div style={{ ...SECTION, marginTop: 20 }}>{t("hubs.wizard.brandColor", "Brand color")}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {["#5142C7", "#2A6FDB", "#1F8A5B", "#D97757", "#B5399A"].map((c) => { const on = (d.branding.primaryColor || "#5142C7") === c; return <button key={c} onClick={() => HubCreationStore.patchBranding({ primaryColor: c })} style={{ width: 28, height: 28, borderRadius: 999, background: c, border: on ? `2px solid ${T.n1}` : `2px solid ${T.border}`, cursor: "pointer" }} />; })}
            </div>
            <div style={{ ...SECTION, marginTop: 20 }}>{t("hubs.wizard.seo", "SEO")}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Field label={t("hubs.wizard.pageTitle", "Page title")}><input value={d.seo.pageTitle || ""} placeholder={i18nT("hubs.wizard.dNameHubEvents.icu", { value0: d.name || "Hub" })} onChange={(e) => HubCreationStore.patchSeo({ pageTitle: e.target.value })} style={inputStyle} /></Field>
              <Field label={t("hubs.wizard.metaDescription", "Meta description")}><textarea value={d.seo.metaDescription || ""} rows={2} onChange={(e) => HubCreationStore.patchSeo({ metaDescription: e.target.value })} style={{ ...inputStyle, height: "auto", padding: "8px 12px", resize: "vertical" }} /></Field>
            </div>
          </div>
        </div>
        {/* Center · live block preview (resizable + responsive) */}
        <div ref={canvasScrollRef} style={{ background: T.canvas, overflow: "auto", padding: "20px 28px", display: "flex", justifyContent: "center", ...(designLock || {}) }}>
          <div ref={pageColRef} style={{ position: "relative", width: vw ?? "100%", maxWidth: "100%", margin: "0 auto", transition: draggingEdge ? "none" : "width 200ms ease" }} onDragOver={(e) => e.preventDefault()}>
            <LandingPreview blocks={blocks} theme={currentTheme} font={d.pageFont} slug={d.slug} hubEvents={eventCards} hostLabel={publicHost("attend")}
              selectedUid={selectedUid} onSelect={setSelectedUid} onRemove={removeBlock} onMove={moveBlock} onDuplicate={duplicate}
              dragOver={dragOver} onDragOverBlock={(u) => setDragOver(u)} onDrop={onDropOnBlock} onCellDrop={onDropToCell}
              onDragStartBlock={(u) => { dragRef.current = { type: null, uid: u }; }} />
            {([-1, 1] as const).map((side) => (
              <div key={side} onPointerDown={startEdgeDrag(side)} title={t("hubs.wizard.dragToTestAnyWidth", "Drag to test any width")}
                style={{ position: "absolute", top: 0, bottom: 0, [side === -1 ? "left" : "right"]: -22, width: 20, display: "flex", alignItems: "center", justifyContent: "center", cursor: "ew-resize", touchAction: "none" } as React.CSSProperties}>
                <span style={{ width: 5, height: 48, borderRadius: 999, background: draggingEdge ? T.p1 : T.n4, transition: "background 120ms" }} />
              </div>
            ))}
            {draggingEdge ? <div style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", zIndex: 5, background: T.n1, color: T.white, fontSize: 11, fontFamily: T.fontMono, borderRadius: 4, padding: "3px 8px", pointerEvents: "none" }}>{liveW}{t("hubs.wizard.px", "px")}</div> : null}
          </div>
        </div>
        {/* Right · events rail (always editable) + inspector (locked when live) */}
        <div style={{ borderLeft: `1px solid ${T.border}`, background: T.white, overflow: "auto", padding: "16px 16px 40px", display: "flex", flexDirection: "column", gap: 18 }}>
          <EventsRail d={d} all={all} canEdit={true} />
          <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, ...designLock }}>
            <div style={SECTION}>{selected ? /* TODO(i18n): replace template-literal with t('hubs.wizard.text4', { ... }) */`Edit · ${palette.find((b) => b.id === selected.type)?.l || selected.type}` : t('hubs.wizard.text5')}</div>
            {selected
              ? <BlockInspector type={selected.type} cfg={selected.cfg} onChange={(patch) => updateCfg(selected.uid, patch)} allBlocks={blocks} />
              : <div style={{ fontSize: 12.5, color: T.soft, lineHeight: 1.5 }}>{t("hubs.wizard.selectABlockInThePreviewToEditItOrAddOne", "Select a block in the preview to edit it, or add one from the left.")}</div>}
          </div>
        </div>
      </div>
    </HubWizardShell>
  );
}

// ── Step 3 · Review & publish ────────────────────────────────────────────────
export function HubCreatePublish() {
  const t = useT();
  const nav = useNav();
  const d = useHubDraft();
  const { events: all } = useEvents();
  const events = all.filter((e) => d.manualEventIds.includes(e.id));
  const checks = requiredHubChecks(d, events.length);
  const missing = checks.filter((c) => !c.done);
  const [busy, setBusy] = useState(false);
  const displayUrl = d.publicUrl || composePublicUrl(d.slug, { bare: true });

  const publish = async () => {
    if (busy || missing.length) return;
    setBusy(true);
    const hub = draftToHub(d);
    const save = await saveHub(hub, !d._editingId).catch(() => ({ ok: false as const }));
    const savedId = (save && "hub" in save && save.hub?.id) ? save.hub.id : hub.id;
    if (save && "ok" in save && save.ok && !d._editingId) HubCreationStore.patch({ _editingId: savedId });
    const pub = await publishHubOp(savedId).catch(() => ({ ok: false as const }));
    setBusy(false);
    if (pub.ok || (save && "ok" in save && save.ok)) {
      HubCreationStore.setPublished();
      refetchHubs();
      toast({ tone: "success", title: t("hubs.wizard.hubPublished", "Hub published"), subtitle: i18nT("hubs.wizard.dNameIsNowLiveAtDisplayurl.icu", { value0: d.name, value1: displayUrl }) });
      nav.go("hub.create.live");
    } else {
      toast({ tone: "info", title: t("hubs.wizard.publishNotAvailableYet", "Publish not available yet"), subtitle: t("hubs.wizard.theHubPublishOpIsnTInTheWiringKitYetNothing", "The hub publish op isn't in the wiring kit yet — nothing went live.") });
    }
  };

  return (
    <HubWizardShell step={2} footer={
      d._published
        ? <Btn iconRight={<Icon.arrow size={14} />} onClick={() => nav.go("hub.create.live")}>{t("hubs.wizard.goToLive", "Go to Live")}</Btn>
        : <Btn icon={<Icon.send size={14} />} disabled={busy || missing.length > 0} title={missing.length ? t("hubs.hubWizard.resolveTheChecklistFirst", "Resolve the checklist first") : undefined} onClick={() => void publish()}>{busy ? t("hubs.hubWizard.publishing", "Publishing…") : t("hubs.hubWizard.publishHub", "Publish hub")}</Btn>
    }>
      <div style={{ maxWidth: 720, margin: "0 auto", width: "100%", padding: "24px 24px 40px", display: "flex", flexDirection: "column", gap: 18 }}>
        <div>
          <h2 style={{ ...tx.h2, fontWeight: 700, margin: 0 }}>{i18nT("hubs.wizard.reviewAmpPublish.rendered", "Review & publish")}</h2>
          <p style={{ ...tx.help, marginTop: 6 }}>{t("hubs.wizard.publishingMakesTheHubPageLiveAtItsPublicUrlOnceLive", "Publishing makes the hub page live at its public URL. Once live, the page design locks — but you can keep adding and removing events without taking it offline.")}</p>
        </div>
        <Card style={{ padding: 18 }}>
          <div style={{ ...SECTION, marginBottom: 12 }}>{t("hubs.wizard.completeness", "Completeness")}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {checks.map((c) => (
              <button key={c.label} onClick={() => nav.go(c.route)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: "none", borderRadius: 8, background: c.done ? "transparent" : T.orange2, cursor: "pointer", textAlign: "left", fontFamily: T.font }}>
                <span style={{ color: c.done ? T.green1 : T.orange1, flexShrink: 0 }}>{c.done ? <Icon.check size={15} /> : <Icon.info size={15} />}</span>
                <span style={{ flex: 1, fontSize: 13.5, color: T.n1 }}>{c.label}</span>
                {!c.done ? <span style={{ fontSize: 12, color: T.p1, fontWeight: 600 }}>{t("hubs.wizard.fix", "Fix →")}</span> : null}
              </button>
            ))}
          </div>
        </Card>
        <Card style={{ padding: 18 }}>
          <div style={{ ...SECTION, marginBottom: 12 }}>{t("hubs.wizard.summary", "Summary")}</div>
          <SummaryLine k="Name" v={d.name || "Untitled hub"} />
          <SummaryLine k="Type" v={d.kind === "location" ? "Location hub" : d.kind === "group" ? "Series hub" : "Workspace hub"} />
          <SummaryLine k="Visibility" v={d.visibilityMode === "public" ? "Public" : "Private (sign-in required)"} />
          <SummaryLine k="Public URL" v={displayUrl} mono />
          <SummaryLine k="Events shown" v={`${events.length} event${events.length === 1 ? "" : "s"}`} />
          <SummaryLine k="Theme" v={d.branding.theme || "Light"} />
        </Card>
      </div>
    </HubWizardShell>
  );
}
function SummaryLine({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0", borderBottom: `1px solid ${T.n6}`, fontSize: 13 }}><span style={{ color: T.soft }}>{k}</span><span style={{ color: T.n1, fontWeight: 500, fontFamily: mono ? T.fontMono : T.font, textAlign: "right" }}>{v}</span></div>;
}

// ── Step 4 · Live ────────────────────────────────────────────────────────────
// ── Recurrence projection (a recurring event = one event on many dates) ──────
function hubAddMonths(d: Date, n: number): Date {
  const x = new Date(d); const day = x.getDate();
  x.setDate(1); x.setMonth(x.getMonth() + n);
  const dim = new Date(x.getFullYear(), x.getMonth() + 1, 0).getDate();
  x.setDate(Math.min(day, dim)); return x;
}
function hubNthWeekday(year: number, month: number, weekday: number, n: number): Date {
  if (n >= 5) { const last = new Date(year, month + 1, 0); const off = (last.getDay() - weekday + 7) % 7; return new Date(year, month, last.getDate() - off); }
  const first = new Date(year, month, 1); const off = (weekday - first.getDay() + 7) % 7;
  return new Date(year, month, 1 + off + (n - 1) * 7);
}
/** Occurrence start-times (ms) for an event: [start] for a one-off, the projected
 *  series dates for a recurring event (bounded by ends-count/date; capped). */
function occurrenceTimes(e: UIEvent, max = 60): number[] {
  const raw = (e.raw || {}) as Record<string, unknown>;
  const startIso = (raw.startsAt as string) || (raw.starts_at as string) || "";
  const s = startIso ? new Date(startIso) : null;
  if (!s || isNaN(s.getTime())) {
    const t = Date.parse(String(e.date || "").replace(/^[A-Za-z]+\s+/, ""));
    return isNaN(t) ? [] : [t];
  }
  const kind = String(raw.repeatKind || raw.repeat_kind || "none");
  if (kind === "none") return [s.getTime()];
  const cfg = ((raw.repeatConfig || raw.repeat_config) as Record<string, unknown>) || {};
  const endsMode = String(cfg.endsMode || "never");
  const endsCount = Number(cfg.endsCount || 0);
  const endsDate = cfg.endsDate ? new Date(String(cfg.endsDate)) : null;
  const limit = endsMode === "count" && endsCount > 0 ? Math.min(endsCount, max) : max;
  const stop = endsMode === "date" && endsDate && !isNaN(endsDate.getTime()) ? endsDate.getTime() : Infinity;
  const out: number[] = [];
  const wd = s.getDay(); const weekN = Math.ceil(s.getDate() / 7);
  if (kind === "custom") {
    const inst = Array.isArray(cfg.customInstances) ? (cfg.customInstances as unknown[]) : [];
    for (const it of inst) { const ds = typeof it === "string" ? it : (it as { start?: string })?.start; const dd = ds ? new Date(ds) : null; if (dd && !isNaN(dd.getTime()) && dd.getTime() <= stop) out.push(dd.getTime()); if (out.length >= limit) break; }
    return out.length ? out : [s.getTime()];
  }
  if (kind === "weekly") {
    const wds: boolean[] = Array.isArray(cfg.weekdays) ? (cfg.weekdays as boolean[]) : [];
    const days = wds.some(Boolean) ? wds.map((b, i) => (b ? i : -1)).filter((i) => i >= 0) : [wd];
    const base = new Date(s); base.setHours(0, 0, 0, 0);
    for (let i = 0; out.length < limit && i < max * 8; i++) {
      const day = new Date(base); day.setDate(base.getDate() + i);
      if (!days.includes(day.getDay())) continue;
      const dt = new Date(day); dt.setHours(s.getHours(), s.getMinutes());
      if (dt.getTime() < s.getTime()) continue;
      if (dt.getTime() > stop) break;
      out.push(dt.getTime());
    }
    return out;
  }
  const step = kind.startsWith("quarter") ? 3 : 1;
  const byWeekday = kind === "month-weekday" || kind === "quarter-weekday";
  for (let i = 0; out.length < limit && i < max * 4; i++) {
    const b = hubAddMonths(s, i * step);
    const dd = byWeekday ? hubNthWeekday(b.getFullYear(), b.getMonth(), wd, weekN) : b;
    dd.setHours(s.getHours(), s.getMinutes());
    if (dd.getTime() > stop) break;
    out.push(dd.getTime());
  }
  return out;
}
const fmtOcc = (ms: number) => (ms === Infinity ? "Date TBD" : new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }));

interface HubEntry { key: string; ev: UIEvent; occMs: number; occCount: number; series: boolean; }

/** Two-pane event manager: All events (search + sort) ↔ Currently live in hub
 *  (search, sort, drag to reorder in Name mode). A recurring event shows as ONE
 *  stacked "series" card in Name mode; in Date mode each occurrence gets its own
 *  card on its date. */
function HubEventsManager({ d, all }: { d: HubDraft; all: UIEvent[] }) {
  const t = useT();
  const [q, setQ] = useState("");
  const [qIn, setQIn] = useState("");
  const [allSort, setAllSort] = useState<"name" | "date">("name");
  const [inSort, setInSort] = useState<"name" | "date">("name");
  const [over, setOver] = useState<"in" | "all" | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const dragId = useRef<string | null>(null);
  const dragFrom = useRef<"in" | "all" | null>(null);

  const byId = new Map(all.map((e) => [e.id, e]));
  const inIds = new Set(d.manualEventIds);
  // Preserve manualEventIds ORDER (Name-mode manual reorder) — not `all` order.
  const inHub = d.manualEventIds.map((id) => byId.get(id)).filter(Boolean) as UIEvent[];

  const nameKey = (e: UIEvent) => e.title.toLowerCase();

  // Build display rows. Name mode: one row per event (series → stacked card).
  // Date mode: one row per occurrence, sorted chronologically (soonest first).
  const buildEntries = (list: UIEvent[], mode: "name" | "date", manualOrder: boolean): HubEntry[] => {
    if (mode === "date") {
      const rows: HubEntry[] = [];
      for (const e of list) {
        const occ = occurrenceTimes(e);
        const times = occ.length ? occ : [Infinity];
        times.forEach((ms, i) => rows.push({ key: `${e.id}#${i}`, ev: e, occMs: ms, occCount: occ.length, series: occ.length > 1 }));
      }
      return rows.sort((a, b) => a.occMs - b.occMs || nameKey(a.ev).localeCompare(nameKey(b.ev)));
    }
    const rows = list.map((e) => { const occ = occurrenceTimes(e); return { key: e.id, ev: e, occMs: occ.length ? occ[0] : Infinity, occCount: occ.length, series: occ.length > 1 }; });
    return manualOrder ? rows : rows.sort((a, b) => nameKey(a.ev).localeCompare(nameKey(b.ev)));
  };

  const allFiltered = all.filter((e) => !inIds.has(e.id) && (!q.trim() || e.title.toLowerCase().includes(q.toLowerCase())));
  const inFiltered = inHub.filter((e) => !qIn.trim() || e.title.toLowerCase().includes(qIn.toLowerCase()));
  const allEntries = buildEntries(allFiltered, allSort, false);
  const inEntries = buildEntries(inFiltered, inSort, inSort === "name");

  // Move `dragEventId` before `beforeId` (or to the end when null). Name mode only.
  const reorderInHub = (dragEventId: string, beforeId: string | null) => {
    const ids = inHub.map((e) => e.id).filter((id) => id !== dragEventId);
    const idx = beforeId ? ids.indexOf(beforeId) : ids.length;
    ids.splice(idx < 0 ? ids.length : idx, 0, dragEventId);
    HubCreationStore.setEventOrder(ids);
  };

  const SortToggle = ({ active, onPick }: { active: "name" | "date"; onPick: (m: "name" | "date") => void }) => (
    <div style={{ display: "inline-flex", border: `1px solid ${T.border}`, borderRadius: 6, overflow: "hidden" }}>
      {(["name", "date"] as const).map((m) => (
        <button key={m} type="button" onClick={() => onPick(m)}
          style={{ padding: "3px 10px", border: "none", background: active === m ? T.p1 : T.white, color: active === m ? T.white : T.n2, fontFamily: T.font, fontSize: 11, fontWeight: 600, cursor: "pointer", borderLeft: m === "date" ? `1px solid ${T.border}` : "none", textTransform: "capitalize" as const }}>{m}</button>
      ))}
    </div>
  );

  const card = (entry: HubEntry, paneKey: "in" | "all", reorderable: boolean) => {
    const ev = entry.ev;
    const action = paneKey === "in" ? "remove" : "add";
    const asStack = entry.series && (paneKey === "all" ? allSort === "name" : inSort === "name");
    const meta = asStack
      ? `${entry.occCount} dates · Series · from ${fmtOcc(entry.occMs)}`
      : `${ev.location.split(" · ")[0]}${entry.occMs !== Infinity ? ` · ${fmtOcc(entry.occMs)}` : ""}${entry.series ? " · series" : ""}`;
    const inner = (
      <div draggable
        onDragStart={() => { dragId.current = ev.id; dragFrom.current = paneKey; }}
        onDragEnd={() => { dragId.current = null; dragFrom.current = null; setOver(null); setOverId(null); }}
        onDragOver={paneKey === "in" && reorderable ? (e) => { if (dragFrom.current === "in") { e.preventDefault(); e.stopPropagation(); setOverId(ev.id); } } : undefined}
        onDrop={paneKey === "in" && reorderable ? (e) => {
          const id = dragId.current; const from = dragFrom.current;
          if (from === "in" && id) { e.preventDefault(); e.stopPropagation(); reorderInHub(id, ev.id); }
          else if (from === "all" && id) { e.preventDefault(); e.stopPropagation(); HubCreationStore.addEvent(id); }
          dragId.current = null; dragFrom.current = null; setOver(null); setOverId(null);
        } : undefined}
        style={{ position: "relative", zIndex: 1, display: "flex", gap: 10, padding: 10, border: `1px solid ${overId === ev.id && paneKey === "in" ? T.p1 : T.border}`, borderRadius: 8, alignItems: "center", background: T.white, cursor: "grab" }}>
        {paneKey === "in" ? <Icon.grip size={14} style={{ color: T.n3, flexShrink: 0 }} /> : null}
        <span style={{ position: "relative", width: 30, height: 30, borderRadius: 5, background: ev.hue, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 11, flexShrink: 0 }}>
          {ev.title.split(" ").map((w) => w[0]).slice(0, 2).join("")}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}>
            {ev.title}
            {asStack ? <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4, color: T.p1, background: T.p4, border: `1px solid ${T.p2}`, borderRadius: 4, padding: "0 5px", flexShrink: 0 }}>{t("hubs.wizard.series", "SERIES")}</span> : null}
          </div>
          <div style={{ fontSize: 11, color: T.soft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{meta}</div>
        </div>
        <IconBtn size={26} label={action === "add" ? t("hubs.hubWizard.addToHub", "Add to hub") : t("hubs.wizard.removeFromHub", "Remove from hub")} onClick={() => action === "add" ? HubCreationStore.addEvent(ev.id) : HubCreationStore.removeEvent(ev.id)}>
          {action === "add" ? <Icon.plus size={13} /> : <Icon.x size={12} />}
        </IconBtn>
      </div>
    );
    if (!asStack) return <div key={entry.key}>{inner}</div>;
    // Stacked series: two cards peeking at the bottom + left edges behind the top card.
    return (
      <div key={entry.key} style={{ position: "relative", marginLeft: 8, marginBottom: 8 }}>
        <div style={{ position: "absolute", inset: 0, transform: "translate(-8px, 8px)", background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, zIndex: 0 }} />
        <div style={{ position: "absolute", inset: 0, transform: "translate(-4px, 4px)", background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, zIndex: 0 }} />
        {inner}
      </div>
    );
  };

  const pane = (key: "in" | "all", title: string, hint: string, entries: HubEntry[], sort: "name" | "date", setSort: (m: "name" | "date") => void, empty: string) => {
    const reorderable = key === "in" && sort === "name";
    return (
      <div
        onDragOver={(e) => { e.preventDefault(); setOver(key); }}
        onDragLeave={() => setOver((o) => (o === key ? null : o))}
        onDrop={(e) => {
          e.preventDefault();
          const id = dragId.current; const from = dragFrom.current;
          dragId.current = null; dragFrom.current = null; setOver(null); setOverId(null);
          if (!id) return;
          // Target-pane semantics: drop in "in" adds (or, Name mode, reorders to end);
          // drop in "all" removes from the hub.
          if (key === "in") { if (from === "all") HubCreationStore.addEvent(id); else if (from === "in" && reorderable) reorderInHub(id, null); }
          else if (from === "in") { HubCreationStore.removeEvent(id); }
        }}
        style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", border: `1.5px solid ${over === key ? T.p1 : T.border}`, borderRadius: 10, background: over === key ? T.p4 : T.n6, transition: "background 120ms, border-color 120ms" }}>
        <div style={{ padding: "12px 14px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.n1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>{title}<span style={{ fontSize: 11, color: T.soft, fontWeight: 600 }}>{entries.length}</span></div>
          <div style={{ fontSize: 11.5, color: T.soft, marginTop: 2 }}>{hint}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <Input placeholder={t("hubs.wizard.findAnEvent", "Find an event…")} value={key === "all" ? q : qIn} onChange={(e) => (key === "all" ? setQ : setQIn)(e.target.value)} style={{ flex: 1, minWidth: 140 }} />
            <span style={{ fontSize: 11, color: T.soft, fontWeight: 600 }}>{t("hubs.wizard.sort", "Sort")}</span>
            <SortToggle active={sort} onPick={setSort} />
          </div>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 8, minHeight: 180 }}>
          {entries.length ? entries.map((en) => card(en, key, reorderable)) : <div style={{ margin: "auto", textAlign: "center", color: T.soft, fontSize: 12.5, padding: 16 }}>{empty}</div>}
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "stretch", minHeight: 340 }}>
      {pane("all", "All events", "Drag into the hub, or use +.", allEntries, allSort, setAllSort, q ? "No matching events." : "Every event is already in this hub.")}
      <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", color: T.n4 }}><Icon.arrow size={18} /></div>
      {pane("in", "Currently live in hub", "Sort by Name to drag-reorder; Date shows each occurrence.", inEntries, inSort, setInSort, qIn ? "No matching events." : "No events yet — drag some in.")}
    </div>
  );
}

export function HubCreateLive() {
  const t = useT();
  const nav = useNav();
  const d = useHubDraft();
  const { events: all } = useEvents();
  const events = all.filter((e) => d.manualEventIds.includes(e.id));
  const live = hubIsLive(d);
  const displayUrl = d.publicUrl || composePublicUrl(d.slug, { bare: true });
  const publicRoute = d.kind === "location" ? "hub.public.location" : d.kind === "group" ? "hub.public.group" : "hub.public.workspace";

  const takeOffline = () => { HubCreationStore.takeOffline(); toast({ tone: "warning", title: t("hubs.wizard.hubTakenOffline", "Hub taken offline"), subtitle: t("hubs.wizard.editThePageThenRePublishToGoLiveAgain", "Edit the page, then re-publish to go live again.") }); nav.go("hub.create.page"); };
  const eventCards = toEventCards(events);
  const currentTheme: LandingTheme = LANDING_THEMES.find((t) => t.id === d.pageTheme)
    || { id: "brand", name: t("hubs.wizard.brand", "Brand"), brand: d.branding.primaryColor || T.p1, hero: [d.branding.primaryColor || "#75ADFA", d.branding.primaryColor || T.p1], font: "DM Sans" };

  return (
    <HubWizardShell step={3} footer={
      live
        ? <Btn variant="secondary" icon={<Icon.eye size={14} />} onClick={() => nav.go(publicRoute, { id: d._editingId || "draft" })}>{t("hubs.wizard.viewLiveHub", "View live hub")}</Btn>
        : <Btn icon={<Icon.send size={14} />} onClick={() => nav.go("hub.create.publish")}>{t("hubs.wizard.goToPublish", "Go to publish")}</Btn>
    }>
      <div style={{ maxWidth: 1040, margin: "0 auto", width: "100%", padding: "24px 24px 40px", display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 16, alignItems: "start" }}>
          {live ? (
            <Card style={{ padding: 18, borderColor: alpha(T.green1, "55"), background: alpha(T.green1, "0d") }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <span style={{ width: 30, height: 30, borderRadius: 8, background: T.green1, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.check size={16} /></span>
                <div><div style={{ fontSize: 15, fontWeight: 700, color: T.n1 }}>{d.name} {t("hubs.wizard.isLive", "is live")}</div><a href={`https://${displayUrl}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12.5, color: T.p1, fontFamily: T.fontMono }}>{displayUrl}</a></div>
              </div>
              <p style={{ fontSize: 13, color: T.n2, lineHeight: 1.5, margin: "8px 0 14px" }}>{t("hubs.wizard.toChangeThePageDesignBrandingOrSeoTakeTheHubOffline", "To change the page design, branding, or SEO, take the hub offline first. Managing which events are in the hub below stays available while live.")}</p>
              <Btn variant="secondary" icon={<Icon.lock size={14} />} onClick={takeOffline}>{t("hubs.wizard.takeOffline", "Take offline")}</Btn>
            </Card>
          ) : (
            <Card style={{ padding: 18 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.n1, marginBottom: 6 }}>{t("hubs.wizard.notPublishedYet", "Not published yet")}</div>
              <p style={{ fontSize: 13, color: T.n2, lineHeight: 1.5, margin: "0 0 14px" }}>{t("hubs.wizard.thisHubIsADraftPublishItToMakeThePageLive", "This hub is a draft. Publish it to make the page live at its public URL.")}</p>
              <Btn icon={<Icon.send size={14} />} onClick={() => nav.go("hub.create.publish")}>{t("hubs.wizard.goToPublish", "Go to publish")}</Btn>
            </Card>
          )}
          <Card style={{ padding: 18 }}>
            <div style={{ ...SECTION, marginBottom: 12 }}>{t("hubs.wizard.analytics30d", "Analytics · 30d")}</div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: T.soft, padding: "5px 0" }}><span>{t("hubs.wizard.visits", "Visits")}</span><b style={{ color: T.n1 }}>{(d.visits ?? 0).toLocaleString()}</b></div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: T.soft, padding: "5px 0" }}><span>{t("hubs.wizard.registrations", "Registrations")}</span><b style={{ color: T.n1 }}>{d.conv ?? 0}</b></div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: T.soft, padding: "5px 0" }}><span>{t("hubs.wizard.eventsInHub", "Events in hub")}</span><b style={{ color: T.n1 }}>{events.length}</b></div>
          </Card>
        </div>
        <Card style={{ padding: 18 }}>
          <div style={{ ...SECTION, marginBottom: 4 }}>{t("hubs.wizard.manageEvents", "Manage events")}</div>
          <p style={{ fontSize: 12.5, color: T.soft, margin: "0 0 14px", lineHeight: 1.5 }}>{t("hubs.wizard.dragEventsBetweenTheTwoListsAddingOrRemovingEventsWorksEven", "Drag events between the two lists — adding or removing events works even while the hub is live.")}</p>
          <HubEventsManager d={d} all={all} />
        </Card>
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div><div style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{t("hubs.wizard.livePagePreview", "Live page preview")}</div><div style={{ fontSize: 11.5, color: T.soft, marginTop: 1 }}>{t("hubs.wizard.theRealPageYourBlocksThemeAndTheEventsCurrentlyInThe", "The real page — your blocks, theme and the events currently in the hub.")}</div></div>
            <Btn variant="secondary" size="sm" icon={<Icon.eye size={13} />} onClick={() => nav.go(publicRoute, { id: d._editingId || "draft" })}>{t("hubs.wizard.openFullPage", "Open full page")}</Btn>
          </div>
          <div style={{ padding: 20, background: T.canvas }}>
            <LandingPreview blocks={d.pageBlocks} theme={currentTheme} font={d.pageFont} slug={d.slug} hubEvents={eventCards} mode="live" chrome={false} />
          </div>
        </Card>
      </div>
    </HubWizardShell>
  );
}
